import React, { useEffect, useState } from "react";
import { FormikProps, withFormik } from "formik";
import { Button } from "@chakra-ui/button";
import { Box, HStack, VStack } from "@chakra-ui/layout";
import * as Yup from "yup";

import { AdminApiClient } from "../api/adminApiClient";
import { CherryHubAdminApi } from "../api/adminApiModels";
import { TextField } from "../components/fields/TextField/TextField";
import { Text, FormControl, FormLabel, Divider } from "@chakra-ui/react";
import { isEmptyStr } from "../util/stringHelper";
import { FormStack } from "../components/FormStack/FormStack";
import { ApiResult } from "../api/dataApiClient";
import {
  ChakraReactSelectField,
  chakraStyles,
} from "../components/fields/SelectField/ChakraReactSelectField";
import { CopyText } from "../components/Utilities/CopyText";
import { TextArea } from "../components/fields/TextArea/TextArea";
import { AllowedValuesBuilder } from "../components/fields/AllowedValuesBuilder/AllowedValuesBuilder";
import { FieldPreview } from "../components/ExtendedFields/FieldPreview";
import { SwitchField } from "../components/fields/SwitchField/SwitchField";
import { IntegerField } from "../components/fields/IntegerField/IntegerField";
import { DecimalField } from "../components/fields/DecimalField/DecimalField";
import { ExtendedFieldConflictModal } from "../components/ExtendedFields/ExtendedFieldConflictModal";
import { TimePeriod } from "../components/RegistrationFormConfiguration/RegistrationFormTimePeriod";
import { Select } from "chakra-react-select";
import { FieldWrapper } from "../components/fields/FieldWrapper/FieldWrapper";

interface ExtendedFieldDetailFormValues {
  fieldName: string;
  description?: string;
  owner?: string;
  fieldType: CherryHubAdminApi.ExtendedFieldType | "";
  writability: CherryHubAdminApi.FieldWritability | "";
  fieldOrder?: number | "";

  // String attributes
  minLength?: number | "";
  maxLength?: number | "";
  regexPattern?: string;
  regexPattern_preset?: string;
  stringAllowedValues?: string;
  stringIsRequired?: boolean;
  stringDefaultValue?: string;

  // Number attributes
  minValue?: number | string | "";
  maxValue?: number | string | "";
  decimalPlaces?: number | "";
  numberAllowedValues?: string;
  numberIsRequired?: boolean;
  numberDefaultValue?: number | string | "";

  // Date attributes
  dateMinValue?: string;
  dateMaxValue?: string;
  dateRelativeMinValue?: CherryHubAdminApi.TimePeriod;
  dateRelativeMaxValue?: CherryHubAdminApi.TimePeriod;
  dateIsRequired?: boolean;
  dateDefaultValue?: string;

  // Boolean attributes
  booleanDefaultValue?: boolean;

  // Time attributes
  timeMinValue?: string;
  timeMaxValue?: string;
  timeIsRequired?: boolean;
}

interface ExtendedFieldDetailFormProps {
  apiClient: AdminApiClient;
  businessId: string;
  field: CherryHubAdminApi.ExtendedFieldDefinition | null | undefined;
  isDisabled?: boolean;
  onCancel: () => void;
  onSuccess: (field: CherryHubAdminApi.ExtendedFieldDefinition) => void;
  onFailure?: (message?: string) => void;
}

const fieldTypeOptions = [
  { label: "Text Input", value: "Text" },
  { label: "Text Area", value: "TextArea" },
  { label: "Text Selection (Dropdown)", value: "TextSelection" },
  { label: "Text Multi-Selection (Dropdown)", value: "TextMultiSelection" },
  { label: "Number Input", value: "Number" },
  { label: "Number Selection (Dropdown)", value: "NumberSelection" },
  { label: "Number Multi-Selection (Dropdown)", value: "NumberMultiSelection" },
  { label: "Date", value: "Date" },
  { label: "Date & Time", value: "DateTime" },
  { label: "Time", value: "Time" },
  { label: "Toggle (Checkbox)", value: "Toggle" },
];

const writabilityOptions = [
  { label: "User Writable", value: "UserWritable" },
  { label: "System Writable", value: "SystemWritable" },
  { label: "Read Only", value: "ReadOnly" },
];

const ownerOptions = [
  { label: "CherryHub", value: "CherryHub" },
  { label: "Source System", value: "SourceSystem" },
];

const REGEX_PATTERNS = [
  {
    label: "Email Address",
    pattern: "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$",
  },
  {
    label: "Australian Phone Number",
    pattern: "^(?:\\+?61|0)[2-478](?:[ -]?[0-9]){8}$",
  },
  {
    label: "Australian Landline Phone Number",
    pattern: "^(+?61|0)(2|3|7|8)(d{1,2}){4}$",
  },
  {
    label: "Australian Mobile Phone Number",
    pattern: "^(+?61|0)4(s?d{2}){4}$",
  },
  {
    label: "Australian Postcode",
    pattern: "^[0-9]{4}$",
  },
  {
    label: "United States Landline Phone Number",
    pattern:
      "^(?:+?1s*(?:[.-]s*)?)?(?:(s*([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])s*)|([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))s*(?:[.-]s*)?([2-9]1[02-9]|[2-9][02-9]{2})s*(?:[.-]s*)?([0-9]{4})$",
  },
  {
    label: "United States Mobile Phone Number",
    pattern: "^+?1[ -]?(?(d{3}))?[ -]?(d{3})[ -]?(d{4})$",
  },
  {
    label: "URL",
    pattern:
      "^(https?://)?(www.)?([a-zA-Z0-9-]+.){1,}[a-zA-Z]{2,}(:[0-9]+)?(/S*)?$",
  },
  {
    label: "Alphanumeric Only",
    pattern: "^[a-zA-Z0-9]+$",
  },
  {
    label: "Letters Only",
    pattern: "^[a-zA-Z]+$",
  },
  {
    label: "Numbers Only",
    pattern: "^[0-9]+$",
  },
  {
    label: "Custom Pattern",
    pattern: "",
  },
];

// Helper functions to check field types
const isStringField = (
  fieldType: CherryHubAdminApi.ExtendedFieldType | ""
): boolean => {
  return (
    fieldType === "Text" ||
    fieldType === "TextArea" ||
    fieldType === "TextSelection" ||
    fieldType === "TextMultiSelection"
  );
};

const isNumberField = (
  fieldType: CherryHubAdminApi.ExtendedFieldType | ""
): boolean => {
  return (
    fieldType === "Number" ||
    fieldType === "NumberSelection" ||
    fieldType === "NumberMultiSelection"
  );
};

const isDateField = (
  fieldType: CherryHubAdminApi.ExtendedFieldType | ""
): boolean => {
  return fieldType === "Date" || fieldType === "DateTime";
};

const isTimeField = (
  fieldType: CherryHubAdminApi.ExtendedFieldType | ""
): boolean => {
  return fieldType === "Time";
};

const isBooleanField = (
  fieldType: CherryHubAdminApi.ExtendedFieldType | ""
): boolean => {
  return fieldType === "Toggle";
};

// Helper function to build field definition from form values
const buildFieldDefinition = (
  values: ExtendedFieldDetailFormValues,
  field: CherryHubAdminApi.ExtendedFieldDefinition | null | undefined,
  version?: number
): Partial<
  | CherryHubAdminApi.CreateExtendedFieldDefinition
  | CherryHubAdminApi.UpdateExtendedFieldDefinition
> => {
  const fieldDef: Partial<
    | CherryHubAdminApi.CreateExtendedFieldDefinition
    | CherryHubAdminApi.UpdateExtendedFieldDefinition
  > = {
    FieldName: values.fieldName,
    Description: values.description || null,
    Owner: (values.owner as "CherryHub" | "SourceSystem") || undefined,
    Writability: values.writability || undefined,
  };

  if (!field) {
    (fieldDef as CherryHubAdminApi.CreateExtendedFieldDefinition).FieldType =
      values.fieldType as CherryHubAdminApi.ExtendedFieldType;
    if (values.fieldOrder) {
      (fieldDef as any).FieldOrder = values.fieldOrder;
    }
    // Add Version if provided
    if (version !== undefined) {
      (fieldDef as CherryHubAdminApi.CreateExtendedFieldDefinition).Version =
        version;
    }
  }

  if (isStringField(values.fieldType)) {
    const allowedValues = values.stringAllowedValues
      ? values.stringAllowedValues
          .split(",")
          .map((v) => v.trim())
          .filter((v) => v)
      : null;

    const stringAttrs: any = {
      MinLength: values.minLength !== "" ? values.minLength : undefined,
      MaxLength: values.maxLength !== "" ? values.maxLength : undefined,
      RegexPattern: values.regexPattern || null,
      AllowedValues: allowedValues,
      IsRequired: values.stringIsRequired ?? false,
    };

    if (values.stringDefaultValue) {
      stringAttrs.DefaultValues = values.stringDefaultValue
        .split(",")
        .map((v) => v.trim())
        .filter((v) => v);
    } else {
      stringAttrs.DefaultValues = null;
    }

    (
      fieldDef as CherryHubAdminApi.CreateExtendedFieldDefinition
    ).StringAttributes = stringAttrs;
  }

  if (isNumberField(values.fieldType)) {
    const allowedValues = values.numberAllowedValues
      ? values.numberAllowedValues
          .split(",")
          .map((v) => parseFloat(v.trim()))
          .filter((v) => !isNaN(v))
      : null;

    const numberAttrs: any = {
      MinValue: values.minValue !== "" ? Number(values.minValue) : null,
      MaxValue: values.maxValue !== "" ? Number(values.maxValue) : null,
      DecimalPlaces: values.decimalPlaces !== "" ? values.decimalPlaces : null,
      AllowedValues: allowedValues,
      IsRequired: values.numberIsRequired ?? false,
    };

    if (
      values.numberDefaultValue !== "" &&
      values.numberDefaultValue !== null &&
      values.numberDefaultValue !== undefined
    ) {
      numberAttrs.DefaultValues = String(values.numberDefaultValue)
        .split(",")
        .map((v) => parseFloat(v.trim()))
        .filter((v) => !isNaN(v));
    } else {
      numberAttrs.DefaultValues = null;
    }

    (
      fieldDef as CherryHubAdminApi.CreateExtendedFieldDefinition
    ).NumberAttributes = numberAttrs;
  }

  if (isDateField(values.fieldType)) {
    // Helper to format date string with time component if needed
    const formatDateValue = (dateValue: string | undefined) => {
      if (!dateValue) return undefined;
      // If it's a date-only value (YYYY-MM-DD), append time component
      if (
        values.fieldType === "Date" &&
        /^\d{4}-\d{2}-\d{2}$/.test(dateValue)
      ) {
        return `${dateValue}T00:00:00`;
      }
      // If it's a datetime value without seconds (YYYY-MM-DDTHH:mm), append seconds
      if (
        values.fieldType === "DateTime" &&
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(dateValue)
      ) {
        return `${dateValue}:00`;
      }
      return dateValue;
    };

    // Helper to normalize TimePeriod Quantity and Period fields
    const normalizeTimePeriod = (
      timePeriod: CherryHubAdminApi.TimePeriod | undefined
    ) => {
      if (!timePeriod) return undefined;
      // If quantity is empty/0 and no period is selected, return undefined
      if (!timePeriod.Quantity && !timePeriod.Period) {
        return undefined;
      }
      return {
        ...timePeriod,
        Quantity: timePeriod.Quantity || 0,
        Period: timePeriod.Period || "Second",
      };
    };

    (
      fieldDef as CherryHubAdminApi.CreateExtendedFieldDefinition
    ).DateAttributes = {
      MinValue: formatDateValue(values.dateMinValue),
      MaxValue: formatDateValue(values.dateMaxValue),
      RelativeMinValue: normalizeTimePeriod(values.dateRelativeMinValue),
      RelativeMaxValue: normalizeTimePeriod(values.dateRelativeMaxValue),
      IsRequired: values.dateIsRequired ?? false,
      DefaultValue: formatDateValue(values.dateDefaultValue),
    };
  }

  if (isBooleanField(values.fieldType)) {
    (
      fieldDef as CherryHubAdminApi.CreateExtendedFieldDefinition
    ).BooleanAttributes = {
      DefaultValue: values.booleanDefaultValue,
    };
  }

  if (isTimeField(values.fieldType)) {
    (
      fieldDef as CherryHubAdminApi.CreateExtendedFieldDefinition
    ).TimeAttributes = {
      MinValue: values.timeMinValue || undefined,
      MaxValue: values.timeMaxValue || undefined,
      IsRequired: values.timeIsRequired ?? false,
      DefaultValue: values.dateDefaultValue || undefined,
    };
  }

  return fieldDef;
};

// Shared submit logic with conflict detection
const submitFieldWithConflictDetection = async (
  values: ExtendedFieldDetailFormValues,
  apiClient: AdminApiClient,
  businessId: string,
  field: CherryHubAdminApi.ExtendedFieldDefinition | null | undefined,
  onSuccess?: (field: CherryHubAdminApi.ExtendedFieldDefinition) => void,
  onFailure?: (message?: string) => void,
  onConflictDetected?: (
    matches: CherryHubAdminApi.ExtendedFieldDefinition[]
  ) => void
): Promise<void> => {
  // Build the field definition
  const fieldDef = buildFieldDefinition(values, field);

  let result: ApiResult<CherryHubAdminApi.ExtendedFieldDefinition>;
  if (field) {
    // Update existing field
    result = await apiClient.updateBusinessExtendedField(
      businessId,
      field.id,
      fieldDef as Partial<CherryHubAdminApi.UpdateExtendedFieldDefinition>
    );
  } else {
    // Create new field
    result = await apiClient.createBusinessExtendedField(
      businessId,
      fieldDef as Partial<CherryHubAdminApi.CreateExtendedFieldDefinition>
    );
  }

  // Check for 409 conflict
  if (!result.ok && result.statusCode === 409 && !field) {
    // For any 409 conflict, attempt to fetch matching fields
    const allFieldsResult = await apiClient.getBusinessExtendedFields(
      businessId,
      true
    );

    if (allFieldsResult.ok) {
      const normalizedName = values.fieldName.replace(/[^\w]/g, "");
      const expectedBaseId =
        `${businessId}.Ext-${normalizedName}-${values.owner}-${values.fieldType}`.toLowerCase();

      // Find all existing fields whose base ID (stripped of version suffix) matches
      const matches = allFieldsResult.data.filter((f) => {
        const existingBaseId = f.id.replace(/-v\d+$/, "").toLowerCase();
        return existingBaseId === expectedBaseId;
      });

      if (onConflictDetected) {
        // Always show conflict modal for 409, even if we don't find matches
        // (this handles edge cases where the API says there's a conflict but we can't find it)
        onConflictDetected(matches);
        return;
      }
    } else {
      // If we can't fetch fields but got 409, still try to show conflict modal
      if (onConflictDetected) {
        onConflictDetected([]);
        return;
      }
    }
  }

  // Handle regular success/failure
  if (result.ok) {
    if (onSuccess) {
      onSuccess(result.data);
    }
  } else {
    if (onFailure) {
      onFailure(result.message);
    }
  }
};

const InnerForm = ({
  isSubmitting,
  isDisabled,
  submitForm,
  isValid,
  onCancel,
  field,
  values,
  setFieldValue,
  apiClient,
  businessId,
  onSuccess,
  onFailure,
}: ExtendedFieldDetailFormProps &
  FormikProps<ExtendedFieldDetailFormValues>) => {
  const isNewField = !field;
  const isDeletedField = !!field?.Meta?.IsDeleted;

  // Conflict resolution state
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [matchingFields, setMatchingFields] = useState<
    CherryHubAdminApi.ExtendedFieldDefinition[]
  >([]);
  const [isResolvingConflict, setIsResolvingConflict] = useState(false);
  const [isCustomSubmitting, setIsCustomSubmitting] = useState(false);

  // Initialize the preset field if it doesn't exist
  if (values.regexPattern_preset === undefined) {
    setFieldValue("regexPattern_preset", "Custom Pattern");
  }

  const selectedPreset = values.regexPattern_preset || "Custom Pattern";

  // Update pattern when preset changes
  useEffect(() => {
    if (selectedPreset && selectedPreset !== "Custom Pattern") {
      const pattern = REGEX_PATTERNS.find((p) => p.label === selectedPreset);
      if (pattern && pattern.pattern) {
        setFieldValue("regexPattern", pattern.pattern);
      }
    }
  }, [selectedPreset, setFieldValue]);

  const selectedPattern = REGEX_PATTERNS.find(
    (p) => p.label === selectedPreset
  );

  const presetOptions = REGEX_PATTERNS.map((pattern) => ({
    label: pattern.label,
    value: pattern.label,
  }));

  // Auto-format min/max/default values when decimal places changes
  useEffect(() => {
    if (
      isNumberField(values.fieldType) &&
      values.decimalPlaces !== "" &&
      values.decimalPlaces !== undefined
    ) {
      const dp = Number(values.decimalPlaces);

      // Format minValue if it exists
      if (values.minValue !== "" && values.minValue !== undefined) {
        const minNum = Number(values.minValue);
        if (!isNaN(minNum)) {
          const formatted = minNum.toFixed(dp);
          if (formatted !== String(values.minValue)) {
            setFieldValue("minValue", formatted);
          }
        }
      }

      // Format maxValue if it exists
      if (values.maxValue !== "" && values.maxValue !== undefined) {
        const maxNum = Number(values.maxValue);
        if (!isNaN(maxNum)) {
          const formatted = maxNum.toFixed(dp);
          if (formatted !== String(values.maxValue)) {
            setFieldValue("maxValue", formatted);
          }
        }
      }

      // Format numberDefaultValue if it exists
      if (
        values.numberDefaultValue !== "" &&
        values.numberDefaultValue !== undefined
      ) {
        const defaultNum = Number(values.numberDefaultValue);
        if (!isNaN(defaultNum)) {
          const formatted = defaultNum.toFixed(dp);
          if (formatted !== String(values.numberDefaultValue)) {
            setFieldValue("numberDefaultValue", formatted);
          }
        }
      }
    }
  }, [values.decimalPlaces, values.fieldType]);

  // Custom submit handler with conflict detection
  const handleSubmitWithConflictDetection = async () => {
    // Validate required fields
    if (
      isEmptyStr(values.fieldName) ||
      !values.fieldType ||
      !values.owner ||
      !values.writability
    ) {
      return;
    }

    setIsCustomSubmitting(true);

    try {
      await submitFieldWithConflictDetection(
        values,
        apiClient,
        businessId,
        field,
        onSuccess,
        onFailure,
        (matches) => {
          setMatchingFields(matches);
          setShowConflictModal(true);
        }
      );
    } finally {
      setIsCustomSubmitting(false);
    }
  };

  // Handle continuing after conflict detection
  const handleContinueWithVersion = async () => {
    setIsResolvingConflict(true);

    // Calculate the highest version from matching fields
    let maxVersion = 0;
    matchingFields.forEach((field) => {
      // Use the Version property from the API
      const version = (field as any).Version || 0;
      if (version > maxVersion) {
        maxVersion = version;
      }
    });
    // If no version exists in matching fields, start with version 2
    const newVersion = maxVersion === 0 ? 2 : maxVersion + 1;

    // Build the field definition with Version
    const fieldDef = buildFieldDefinition(values, field, newVersion);

    // Retry the creation with Version
    const result = await apiClient.createBusinessExtendedField(
      businessId,
      fieldDef as Partial<CherryHubAdminApi.CreateExtendedFieldDefinition>
    );

    setIsResolvingConflict(false);
    setShowConflictModal(false);

    if (result.ok) {
      if (onSuccess) {
        onSuccess(result.data);
      }
    } else {
      if (onFailure) {
        onFailure(result.message);
      }
    }
  };

  return (
    <Box w="100%" alignSelf="flex-start">
      <FormStack>
        {field && (
          <HStack w="100%">
            <Box flexGrow={2} pb={4}>
              <CopyText text={field?.id!} hover adjustMargin>
                <Text fontSize="md" color="cherryUi.600">
                  {field?.id}
                </Text>
              </CopyText>
              <Text fontSize="xl" color="cherryUi.600">
                <b>{field?.FieldName}</b>
              </Text>
            </Box>

            <Box>
              <Button
                colorScheme="cherryButton"
                isLoading={isCustomSubmitting}
                disabled={isCustomSubmitting || !isValid}
                onClick={handleSubmitWithConflictDetection}
              >
                Save
              </Button>
            </Box>

            <Box>
              <Button disabled={isSubmitting} onClick={onCancel}>
                Cancel
              </Button>
            </Box>
          </HStack>
        )}

        <VStack w="100%" maxW="1200px" alignItems="flex-start" spacing={6}>
          <VStack w="100%" alignItems="flex-start" spacing={4}>
            <Text fontSize="lg" fontWeight="bold" color="cherryUi.600">
              Basic Information
            </Text>

            <TextField
              horizontal
              valueProps={{ flex: 1 }}
              label="Field Name"
              name="fieldName"
              placeholder="Name of the field"
              isRequired
            />

            <TextArea
              horizontal
              valueProps={{ flex: 1 }}
              label="Description"
              name="description"
              placeholder="Description for tooltips"
              rows={3}
            />

            <ChakraReactSelectField
              horizontal
              valueProps={{ flex: 1 }}
              label="Owner"
              name="owner"
              selectedOptionColor="blue"
              options={ownerOptions}
              placeholder="Select owner..."
              isRequired
              isDisabled={!isNewField}
            />

            <ChakraReactSelectField
              horizontal
              valueProps={{ flex: 1 }}
              label="Field Type"
              name="fieldType"
              selectedOptionColor="blue"
              isRequired
              options={fieldTypeOptions}
              isDisabled={!isNewField}
            />

            <ChakraReactSelectField
              horizontal
              valueProps={{ flex: 1 }}
              label="Writability"
              name="writability"
              selectedOptionColor="blue"
              options={writabilityOptions}
              isRequired
            />
          </VStack>

          <VStack w="100%" alignItems="flex-start" spacing={4}>
            {isStringField(values.fieldType) && (
              <>
                <Text
                  fontSize="lg"
                  fontWeight="bold"
                  color="cherryUi.600"
                  pt={4}
                >
                  String Field Configuration
                </Text>

                <SwitchField
                  horizontal
                  valueProps={{ flex: 1 }}
                  label="Is Required"
                  name="stringIsRequired"
                />

                {(values.fieldType === "Text" ||
                  values.fieldType === "TextArea") && (
                  <>
                    <IntegerField
                      horizontal
                      valueProps={{ flex: 1 }}
                      label="Min Length"
                      name="minLength"
                      placeholder="Minimum length"
                      min={0}
                    />

                    <IntegerField
                      horizontal
                      valueProps={{ flex: 1 }}
                      label="Max Length"
                      name="maxLength"
                      placeholder="Maximum length"
                      min={0}
                    />

                    <ChakraReactSelectField
                      horizontal
                      valueProps={{ flex: 1 }}
                      label="Regex Pattern Preset"
                      name="regexPattern_preset"
                      selectedOptionColor="blue"
                      options={presetOptions}
                      helperText="Selecting a preset option will insert its regex into the field below. Only the final pattern in the field below is saved."
                    />

                    <TextField
                      horizontal
                      valueProps={{ flex: 1 }}
                      label="Regex Pattern"
                      name="regexPattern"
                      placeholder="Enter validation pattern..."
                      fontFamily="monospace"
                      onChange={(e) => {
                        // Update the pattern value
                        setFieldValue("regexPattern", e.target.value);

                        const currentPreset = values.regexPattern_preset;
                        // When user manually edits the pattern, switch to custom preset
                        if (currentPreset !== "Custom Pattern") {
                          setFieldValue(
                            "regexPattern_preset",
                            "Custom Pattern"
                          );
                        }
                      }}
                    />

                    {values.fieldType === "TextArea" ? (
                      <TextArea
                        horizontal
                        valueProps={{ flex: 1 }}
                        label="Default Value"
                        name="stringDefaultValue"
                        placeholder="Default value..."
                        rows={3}
                      />
                    ) : (
                      <TextField
                        horizontal
                        valueProps={{ flex: 1 }}
                        label="Default Value"
                        name="stringDefaultValue"
                        placeholder="Default value..."
                      />
                    )}
                  </>
                )}

                {(values.fieldType === "TextSelection" ||
                  values.fieldType === "TextMultiSelection") && (
                  <>
                    <AllowedValuesBuilder
                      name="stringAllowedValues"
                      label="Allowed Values"
                      type="string"
                      placeholder="Add option..."
                    />

                    {values.fieldType === "TextSelection" ? (
                      <ChakraReactSelectField
                        horizontal
                        valueProps={{ flex: 1 }}
                        label="Default Value"
                        name="stringDefaultValue"
                        selectedOptionColor="blue"
                        options={
                          values.stringAllowedValues
                            ? values.stringAllowedValues
                                .split(",")
                                .map((v) => v.trim())
                                .filter((v) => v)
                                .map((v) => ({ label: v, value: v }))
                            : []
                        }
                        placeholder="Select default value..."
                        allowEmpty="None"
                      />
                    ) : (
                      <FieldWrapper
                        name="stringDefaultValue"
                        label="Default Value"
                        horizontal
                        valueProps={{ flex: 1 }}
                      >
                        {({ field }) => (
                          <HStack w="100%">
                            <Box flex={1} bgColor="white">
                              <Select
                                isMulti
                                value={
                                  field.value
                                    ? field.value
                                        .split(",")
                                        .map((v: string) => v.trim())
                                        .filter((v: string) => v)
                                        .map((v: string) => ({
                                          label: v,
                                          value: v,
                                        }))
                                    : []
                                }
                                onChange={(selected: any) => {
                                  const selectedValues = selected
                                    ? selected
                                        .map((s: any) => s.value)
                                        .join(", ")
                                    : "";
                                  setFieldValue(
                                    "stringDefaultValue",
                                    selectedValues
                                  );
                                }}
                                options={
                                  values.stringAllowedValues
                                    ? values.stringAllowedValues
                                        .split(",")
                                        .map((v) => v.trim())
                                        .filter((v) => v)
                                        .map((v) => ({ label: v, value: v }))
                                    : []
                                }
                                placeholder="Select default values..."
                                size="sm"
                                chakraStyles={chakraStyles}
                              />
                            </Box>
                          </HStack>
                        )}
                      </FieldWrapper>
                    )}
                  </>
                )}
              </>
            )}

            {isNumberField(values.fieldType) && (
              <>
                <Text
                  fontSize="lg"
                  fontWeight="bold"
                  color="cherryUi.600"
                  pt={4}
                >
                  Number Field Configuration
                </Text>

                <SwitchField
                  horizontal
                  valueProps={{ flex: 1 }}
                  label="Is Required"
                  name="numberIsRequired"
                />

                {values.fieldType === "Number" && (
                  <>
                    <IntegerField
                      horizontal
                      valueProps={{ flex: 1 }}
                      label="Decimal Places"
                      name="decimalPlaces"
                      placeholder="Number of decimal places"
                      min={0}
                      max={10}
                    />

                    <DecimalField
                      horizontal
                      valueProps={{ flex: 1 }}
                      label="Min Value"
                      name="minValue"
                      placeholder="Minimum value"
                      decimalPlaces={
                        values.decimalPlaces !== ""
                          ? Number(values.decimalPlaces)
                          : 0
                      }
                    />

                    <DecimalField
                      horizontal
                      valueProps={{ flex: 1 }}
                      label="Max Value"
                      name="maxValue"
                      placeholder="Maximum value"
                      decimalPlaces={
                        values.decimalPlaces !== ""
                          ? Number(values.decimalPlaces)
                          : 0
                      }
                    />

                    <DecimalField
                      horizontal
                      valueProps={{ flex: 1 }}
                      label="Default Value"
                      name="numberDefaultValue"
                      placeholder="Default value..."
                      decimalPlaces={
                        values.decimalPlaces !== ""
                          ? Number(values.decimalPlaces)
                          : 0
                      }
                    />
                  </>
                )}

                {(values.fieldType === "NumberSelection" ||
                  values.fieldType === "NumberMultiSelection") && (
                  <>
                    <AllowedValuesBuilder
                      name="numberAllowedValues"
                      label="Allowed Values"
                      type="number"
                      placeholder="Add number..."
                      helperText="Add numbers that users can select from the dropdown"
                    />

                    {values.fieldType === "NumberSelection" ? (
                      <ChakraReactSelectField
                        horizontal
                        valueProps={{ flex: 1 }}
                        label="Default Value"
                        name="numberDefaultValue"
                        selectedOptionColor="blue"
                        options={
                          values.numberAllowedValues
                            ? values.numberAllowedValues
                                .split(",")
                                .map((v) => v.trim())
                                .filter((v) => v)
                                .map((v) => ({ label: v, value: v }))
                            : []
                        }
                        placeholder="Select default value..."
                        allowEmpty="None"
                      />
                    ) : (
                      <FieldWrapper
                        name="numberDefaultValue"
                        label="Default Value"
                        horizontal
                        valueProps={{ flex: 1 }}
                      >
                        {({ field }) => (
                          <HStack w="100%">
                            <Box flex={1} bgColor="white">
                              <Select
                                isMulti
                                value={
                                  field.value
                                    ? String(field.value)
                                        .split(",")
                                        .map((v: string) => v.trim())
                                        .filter((v: string) => v)
                                        .map((v: string) => ({
                                          label: v,
                                          value: v,
                                        }))
                                    : []
                                }
                                onChange={(selected: any) => {
                                  const selectedValues = selected
                                    ? selected
                                        .map((s: any) => s.value)
                                        .join(", ")
                                    : "";
                                  setFieldValue(
                                    "numberDefaultValue",
                                    selectedValues
                                  );
                                }}
                                options={
                                  values.numberAllowedValues
                                    ? values.numberAllowedValues
                                        .split(",")
                                        .map((v) => v.trim())
                                        .filter((v) => v)
                                        .map((v) => ({ label: v, value: v }))
                                    : []
                                }
                                placeholder="Select default values..."
                                size="sm"
                                chakraStyles={chakraStyles}
                              />
                            </Box>
                          </HStack>
                        )}
                      </FieldWrapper>
                    )}
                  </>
                )}
              </>
            )}

            {isDateField(values.fieldType) && (
              <>
                <Text
                  fontSize="lg"
                  fontWeight="bold"
                  color="cherryUi.600"
                  pt={4}
                >
                  {values.fieldType === "DateTime"
                    ? "Date & Time Field Configuration"
                    : "Date Field Configuration"}
                </Text>

                <SwitchField
                  horizontal
                  valueProps={{ flex: 1 }}
                  label="Is Required"
                  name="dateIsRequired"
                />

                <Box
                  w="100%"
                  p={4}
                  border="1px solid"
                  borderColor="cherryButtonDark.100"
                  borderRadius="md"
                >
                  <Text fontSize="sm">
                    <b>Fixed Dates</b>
                  </Text>
                  <Divider mb={2} />
                  <VStack w="100%" alignItems="flex-start" spacing={4}>
                    <TextField
                      horizontal
                      valueProps={{ flex: 1 }}
                      label={
                        values.fieldType === "DateTime"
                          ? "Min Date & Time"
                          : "Min Date"
                      }
                      name="dateMinValue"
                      placeholder={
                        values.fieldType === "DateTime"
                          ? "Minimum date and time"
                          : "Minimum date"
                      }
                      type={
                        values.fieldType === "DateTime"
                          ? "datetime-local"
                          : "date"
                      }
                    />

                    <TextField
                      horizontal
                      valueProps={{ flex: 1 }}
                      label={
                        values.fieldType === "DateTime"
                          ? "Max Date & Time"
                          : "Max Date"
                      }
                      name="dateMaxValue"
                      placeholder={
                        values.fieldType === "DateTime"
                          ? "Maximum date and time"
                          : "Maximum date"
                      }
                      type={
                        values.fieldType === "DateTime"
                          ? "datetime-local"
                          : "date"
                      }
                    />
                  </VStack>
                </Box>

                <Box
                  w="100%"
                  p={4}
                  border="1px solid"
                  borderColor="cherryButtonDark.100"
                  borderRadius="md"
                >
                  <Text fontSize="sm">
                    <b>Relative Dates</b>
                  </Text>
                  <Divider mb={2} />
                  <HStack w="100%" alignItems="flex-start" spacing={4}>
                    <TimePeriod
                      headerLabel={
                        values.fieldType === "DateTime"
                          ? "Relative Min Date & Time"
                          : "Relative Min Date"
                      }
                      fieldName="dateRelativeMinValue"
                      timePeriod={values.dateRelativeMinValue}
                      flex={1}
                    />

                    <TimePeriod
                      headerLabel={
                        values.fieldType === "DateTime"
                          ? "Relative Max Date & Time"
                          : "Relative Max Date"
                      }
                      fieldName="dateRelativeMaxValue"
                      timePeriod={values.dateRelativeMaxValue}
                      flex={1}
                    />
                  </HStack>
                </Box>

                <TextField
                  horizontal
                  valueProps={{ flex: 1 }}
                  label="Default Value"
                  name="dateDefaultValue"
                  placeholder={
                    values.fieldType === "DateTime"
                      ? "Default date and time"
                      : "Default date"
                  }
                  type={
                    values.fieldType === "DateTime" ? "datetime-local" : "date"
                  }
                />
              </>
            )}

            {isTimeField(values.fieldType) && (
              <>
                <Text
                  fontSize="lg"
                  fontWeight="bold"
                  color="cherryUi.600"
                  pt={4}
                >
                  Time Field Configuration
                </Text>

                <SwitchField
                  horizontal
                  valueProps={{ flex: 1 }}
                  label="Is Required"
                  name="timeIsRequired"
                />

                <TextField
                  horizontal
                  valueProps={{ flex: 1 }}
                  label="Min Time"
                  name="timeMinValue"
                  placeholder="Minimum time"
                  type="time"
                />

                <TextField
                  horizontal
                  valueProps={{ flex: 1 }}
                  label="Max Time"
                  name="timeMaxValue"
                  placeholder="Maximum time"
                  type="time"
                />

                <TextField
                  horizontal
                  valueProps={{ flex: 1 }}
                  label="Default Value"
                  name="dateDefaultValue"
                  placeholder="Default time"
                  type="time"
                />
              </>
            )}

            {isBooleanField(values.fieldType) && (
              <>
                <Text
                  fontSize="lg"
                  fontWeight="bold"
                  color="cherryUi.600"
                  pt={4}
                >
                  Toggle Field Configuration
                </Text>

                <SwitchField
                  horizontal
                  valueProps={{ flex: 1 }}
                  label="Default Value"
                  name="booleanDefaultValue"
                />
              </>
            )}
          </VStack>

          <VStack w="100%" alignItems="flex-start" spacing={4}>
            <Text fontSize="lg" fontWeight="bold" color="cherryUi.600">
              Field Preview
            </Text>
            <Text fontSize="xs" color="gray.600">
              This is an approximation of how the field will appear to users.
              Any changes made to the settings above will automatically be
              reflected in the preview below in real-time.
            </Text>
            {values.fieldType && (
              <FieldPreview
                valueProps={{ flex: 1 }}
                fieldType={values.fieldType}
                fieldName={values.fieldName}
                description={values.description}
                minLength={values.minLength}
                maxLength={values.maxLength}
                regexPattern={values.regexPattern}
                stringAllowedValues={values.stringAllowedValues}
                minValue={values.minValue}
                maxValue={values.maxValue}
                decimalPlaces={values.decimalPlaces}
                numberAllowedValues={values.numberAllowedValues}
                dateMinValue={values.dateMinValue}
                dateMaxValue={values.dateMaxValue}
                timeMinValue={values.timeMinValue}
                timeMaxValue={values.timeMaxValue}
                booleanDefaultValue={values.booleanDefaultValue}
              />
            )}
          </VStack>
        </VStack>

        <HStack width="100%" justifyContent="center" spacing="3" pt="8">
          <Button
            colorScheme="cherryButton"
            isLoading={isCustomSubmitting}
            disabled={isCustomSubmitting || !isValid || isDeletedField}
            onClick={handleSubmitWithConflictDetection}
          >
            Save
          </Button>
          <Button disabled={isCustomSubmitting} onClick={onCancel}>
            Cancel
          </Button>
        </HStack>
      </FormStack>

      {/* Conflict Resolution Modal */}
      <ExtendedFieldConflictModal
        isOpen={showConflictModal}
        onClose={() => setShowConflictModal(false)}
        onContinue={handleContinueWithVersion}
        matchingFields={matchingFields}
        isLoading={isResolvingConflict}
      />
    </Box>
  );
};

export const ExtendedFieldDetailForm = withFormik<
  ExtendedFieldDetailFormProps,
  ExtendedFieldDetailFormValues
>({
  mapPropsToValues: ({ field }) => {
    // Helper to format date/datetime values for HTML inputs
    const formatDateForInput = (
      dateStr: string | undefined | null,
      fieldType: string | undefined
    ): string => {
      if (!dateStr) return "";

      // For Date fields, HTML input expects YYYY-MM-DD
      if (fieldType === "Date") {
        // If the date string contains time (ISO format), extract just the date part
        return dateStr.split("T")[0];
      }

      // For DateTime fields, HTML input expects YYYY-MM-DDTHH:mm (no seconds)
      if (fieldType === "DateTime") {
        // Remove seconds if present (e.g., "2025-01-15T00:00:00" -> "2025-01-15T00:00")
        if (dateStr.includes(":")) {
          const parts = dateStr.split(":");
          if (parts.length >= 2) {
            return `${parts[0]}:${parts[1]}`;
          }
        }
        return dateStr;
      }

      return dateStr;
    };

    // Helper to format time values for HTML time inputs
    const formatTimeForInput = (timeStr: string | undefined | null): string => {
      if (!timeStr) return "";

      // Extract time from ISO string like "1970-01-01T14:30:00" or "2025-01-15T14:30:00Z"
      const timeMatch = timeStr.match(/T(\d{2}:\d{2})/);
      if (timeMatch) {
        return timeMatch[1]; // Returns "14:30"
      }

      // HTML time input expects HH:mm format
      // If we have HH:mm:ss, remove the seconds
      if (timeStr.includes(":")) {
        const parts = timeStr.split(":");
        if (parts.length >= 2) {
          return `${parts[0]}:${parts[1]}`;
        }
      }

      return timeStr;
    };

    const stringAllowedValues =
      field?.StringAttributes?.AllowedValues?.join(", ") || "";
    const numberAllowedValues =
      field?.NumberAttributes?.AllowedValues?.join(", ") || "";

    // Format min/max/default values according to decimal places
    const decimalPlaces = field?.NumberAttributes?.DecimalPlaces;
    let minValue: number | string | "" =
      field?.NumberAttributes?.MinValue ?? "";
    let maxValue: number | string | "" =
      field?.NumberAttributes?.MaxValue ?? "";

    // Handle default value - convert array to comma-separated string for form display
    let numberDefaultValue: number | string | "" = "";
    const numberAttrs = field?.NumberAttributes as any;

    if (numberAttrs) {
      if (
        numberAttrs.DefaultValues &&
        Array.isArray(numberAttrs.DefaultValues)
      ) {
        numberDefaultValue = numberAttrs.DefaultValues.join(", ");
      }
    }

    if (
      decimalPlaces !== undefined &&
      decimalPlaces !== null &&
      decimalPlaces >= 0
    ) {
      if (minValue !== "" && minValue !== null) {
        minValue = Number(minValue).toFixed(decimalPlaces);
      }
      if (maxValue !== "" && maxValue !== null) {
        maxValue = Number(maxValue).toFixed(decimalPlaces);
      }
      if (
        numberDefaultValue !== "" &&
        numberDefaultValue !== null &&
        typeof numberDefaultValue === "number"
      ) {
        // Only format single number values, not comma-separated multi-select strings
        numberDefaultValue = Number(numberDefaultValue).toFixed(decimalPlaces);
      }
    }

    return {
      fieldName: field?.FieldName ?? "",
      description: field?.Description ?? "",
      owner: field?.Owner ?? "",
      fieldType: field?.FieldType ?? "",
      writability: field?.Writability ?? "",
      fieldOrder: field?.FieldOrder ?? "",

      // String attributes
      minLength: field?.StringAttributes?.MinLength ?? "",
      maxLength: field?.StringAttributes?.MaxLength ?? "",
      regexPattern: field?.StringAttributes?.RegexPattern ?? "",
      regexPattern_preset: "Custom Pattern",
      stringAllowedValues,
      stringIsRequired: field?.StringAttributes?.IsRequired ?? false,
      stringDefaultValue: (() => {
        const stringAttrs = field?.StringAttributes as any;
        if (!stringAttrs) return "";

        if (
          stringAttrs.DefaultValues &&
          Array.isArray(stringAttrs.DefaultValues)
        ) {
          return stringAttrs.DefaultValues.join(", ");
        }
        return "";
      })(),

      // Number attributes
      minValue,
      maxValue,
      decimalPlaces: decimalPlaces ?? "",
      numberAllowedValues,
      numberIsRequired: field?.NumberAttributes?.IsRequired ?? false,
      numberDefaultValue,

      // Date attributes
      dateMinValue: formatDateForInput(
        field?.DateAttributes?.MinValue,
        field?.FieldType
      ),
      dateMaxValue: formatDateForInput(
        field?.DateAttributes?.MaxValue,
        field?.FieldType
      ),
      dateRelativeMinValue: field?.DateAttributes?.RelativeMinValue,
      dateRelativeMaxValue: field?.DateAttributes?.RelativeMaxValue,
      dateIsRequired: field?.DateAttributes?.IsRequired ?? false,
      dateDefaultValue:
        field?.FieldType === "Time"
          ? formatTimeForInput(field?.TimeAttributes?.DefaultValue)
          : formatDateForInput(
              field?.DateAttributes?.DefaultValue,
              field?.FieldType
            ),

      // Boolean attributes
      booleanDefaultValue: field?.BooleanAttributes?.DefaultValue ?? false,

      // Time attributes
      timeMinValue: formatTimeForInput(field?.TimeAttributes?.MinValue),
      timeMaxValue: formatTimeForInput(field?.TimeAttributes?.MaxValue),
      timeIsRequired: field?.TimeAttributes?.IsRequired ?? false,
    };
  },

  validationSchema: Yup.object().shape({
    fieldName: Yup.string().required("Field name is required"),
    fieldType: Yup.string().required("Field type is required"),
    owner: Yup.string().required("Owner is required"),
    writability: Yup.string().required("Writability is required"),
  }),

  handleSubmit: async (values, { props, setFieldError }) => {
    if (isEmptyStr(values.fieldName)) {
      setFieldError("fieldName", "Field name is required.");
      return;
    }

    if (!values.fieldType) {
      setFieldError("fieldType", "Field type is required.");
      return;
    }

    if (!values.owner) {
      setFieldError("owner", "Owner is required.");
      return;
    }

    if (!values.writability) {
      setFieldError("writability", "Writability is required.");
      return;
    }

    // Use the shared submit function with conflict detection
    // Note: When called from Formik's handleSubmit, we don't have access to the
    // InnerForm's state setters for the conflict modal, so we just show the error
    // message directly. The explicit button clicks will use the custom handler
    // which properly shows the conflict modal.
    await submitFieldWithConflictDetection(
      values,
      props.apiClient,
      props.businessId,
      props.field,
      props.onSuccess,
      props.onFailure,
      // Conflict detected callback - show error message instead of modal
      // since we don't have access to InnerForm's state from here
      () => {
        if (props.onFailure) {
          props.onFailure(
            "An ExtendedFieldDefinition with the same field name, owner and field type already exists. Please use the Save button to see conflict resolution options."
          );
        }
      }
    );
  },

  validateOnBlur: false,
  validateOnMount: false,
  validateOnChange: true,
})(InnerForm);
